import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import api from '../../services/api';
import { Button } from '../../components/ui/button';
import {
  ArrowLeft,
  Play,
  Pause,
  SkipForward,
  SkipBack,
  Check,
  Clock,
  Camera,
  NoteBlank,
  Warning,
  X,
  CaretDown,
  CaretUp,
  Image,
  ArrowClockwise,
  CheckCircle,
  Circle,
  DeviceTabletSpeaker,
  Desktop,
  ArrowRight
} from '@phosphor-icons/react';
import { toast } from 'sonner';

// ==================== HOOKS ====================

const useTabletDetect = () => {
  const [isTablet, setIsTablet] = useState(false);
  useEffect(() => {
    const check = () => {
      const hasTouch = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
      const midWidth = window.innerWidth >= 600 && window.innerWidth <= 1366;
      setIsTablet(hasTouch && midWidth);
    };
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);
  return isTablet;
};

const useSwipe = (onLeft, onRight, ref) => {
  const startX = useRef(0);
  const startY = useRef(0);

  useEffect(() => {
    const el = ref?.current;
    if (!el) return;
    const onStart = (e) => {
      startX.current = e.touches[0].clientX;
      startY.current = e.touches[0].clientY;
    };
    const onEnd = (e) => {
      const dx = e.changedTouches[0].clientX - startX.current;
      const dy = e.changedTouches[0].clientY - startY.current;
      if (Math.abs(dx) > Math.abs(dy) && Math.abs(dx) > 60) {
        if (dx < 0) onLeft?.();
        else onRight?.();
      }
    };
    el.addEventListener('touchstart', onStart, { passive: true });
    el.addEventListener('touchend', onEnd, { passive: true });
    return () => {
      el.removeEventListener('touchstart', onStart);
      el.removeEventListener('touchend', onEnd);
    };
  }, [onLeft, onRight, ref]);
};

// ==================== SUB-COMPONENTS ====================

const CountdownTimer = ({ durationMinutes, onComplete, large }) => {
  const [totalSeconds, setTotalSeconds] = useState(0);
  const [running, setRunning] = useState(false);
  const intervalRef = useRef(null);

  useEffect(() => {
    return () => { if (intervalRef.current) clearInterval(intervalRef.current); };
  }, []);

  const start = () => {
    if (totalSeconds === 0 && durationMinutes) setTotalSeconds(durationMinutes * 60);
    setRunning(true);
  };

  useEffect(() => {
    if (running && totalSeconds > 0) {
      intervalRef.current = setInterval(() => {
        setTotalSeconds(prev => {
          if (prev <= 1) {
            clearInterval(intervalRef.current);
            setRunning(false);
            onComplete?.();
            toast.success('Timer complete!', { duration: 5000 });
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => { if (intervalRef.current) clearInterval(intervalRef.current); };
  }, [running]);

  const pause = () => { clearInterval(intervalRef.current); setRunning(false); };
  const reset = () => { clearInterval(intervalRef.current); setRunning(false); setTotalSeconds(0); };

  const mins = Math.floor(totalSeconds / 60);
  const secs = totalSeconds % 60;
  const display = `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
  const label = durationMinutes >= 60 ? `${Math.round(durationMinutes / 60)}h` : `${durationMinutes}m`;

  if (!durationMinutes) return null;

  return (
    <div className={`flex items-center gap-3 mt-3 ${large ? 'mt-5' : ''}`} data-testid="step-timer">
      <div className={large ? 'timer-display text-4xl' : 'timer-display text-lg'}>{display}</div>
      {totalSeconds === 0 && !running && (
        <button
          className={`bench-touch-btn bench-touch-btn--primary ${large ? 'bench-touch-btn--lg' : ''}`}
          onClick={start}
          data-testid="start-step-timer"
        >
          <Play size={large ? 20 : 12} weight="fill" /> {label}
        </button>
      )}
      {totalSeconds > 0 && running && (
        <button className={`bench-touch-btn bench-touch-btn--secondary ${large ? 'bench-touch-btn--lg' : ''}`} onClick={pause}>
          <Pause size={large ? 20 : 12} weight="fill" /> Pause
        </button>
      )}
      {totalSeconds > 0 && !running && (
        <button className={`bench-touch-btn bench-touch-btn--primary ${large ? 'bench-touch-btn--lg' : ''}`} onClick={() => setRunning(true)}>
          <Play size={large ? 20 : 12} weight="fill" /> Resume
        </button>
      )}
      {totalSeconds > 0 && (
        <button className="bench-touch-btn bench-touch-btn--ghost" onClick={reset}>
          <ArrowClockwise size={large ? 18 : 12} />
        </button>
      )}
    </div>
  );
};

const StepNoteForm = ({ runId, stepNum, onAdded, large }) => {
  const [text, setText] = useState('');
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!text.trim()) return;
    setSaving(true);
    try {
      await api.updateRunStep(runId, stepNum, 'add_note', { text: text.trim() });
      setText('');
      onAdded();
      toast.success('Note added');
    } catch { toast.error('Failed to add note'); }
    finally { setSaving(false); }
  };

  return (
    <div className="flex gap-2 mt-3" data-testid="step-note-form">
      <input
        value={text}
        onChange={e => setText(e.target.value)}
        onKeyDown={e => e.key === 'Enter' && submit()}
        placeholder="Add a note..."
        className={`input-field flex-1 ${large ? 'text-base p-3' : 'text-sm'}`}
        data-testid="step-note-input"
      />
      <button className={`bench-touch-btn bench-touch-btn--primary ${large ? 'bench-touch-btn--lg' : ''}`} onClick={submit} disabled={saving || !text.trim()}>
        {saving ? '...' : 'Add'}
      </button>
    </div>
  );
};

const StepDeviationForm = ({ runId, stepNum, onAdded, large }) => {
  const [text, setText] = useState('');
  const [expected, setExpected] = useState('');
  const [actual, setActual] = useState('');
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!text.trim()) return;
    setSaving(true);
    try {
      await api.updateRunStep(runId, stepNum, 'add_deviation', { text: text.trim(), expected, actual });
      setText(''); setExpected(''); setActual('');
      onAdded();
      toast.success('Deviation logged');
    } catch { toast.error('Failed to log deviation'); }
    finally { setSaving(false); }
  };

  const inputCls = `input-field w-full ${large ? 'text-base p-3' : 'text-sm'}`;

  return (
    <div className="space-y-2 mt-3" data-testid="step-deviation-form">
      <input value={text} onChange={e => setText(e.target.value)} placeholder="What deviated?" className={inputCls} data-testid="deviation-text-input" />
      <div className="grid grid-cols-2 gap-2">
        <input value={expected} onChange={e => setExpected(e.target.value)} placeholder="Expected" className={inputCls} />
        <input value={actual} onChange={e => setActual(e.target.value)} placeholder="Actual" className={inputCls} />
      </div>
      <button className={`bench-touch-btn bench-touch-btn--primary ${large ? 'bench-touch-btn--lg' : ''}`} onClick={submit} disabled={saving || !text.trim()}>
        {saving ? '...' : 'Log Deviation'}
      </button>
    </div>
  );
};

const StepPhotoUpload = ({ runId, stepNum, onUploaded, large }) => {
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      await api.uploadStepPhoto(runId, stepNum, file, '');
      onUploaded();
      toast.success('Photo attached');
    } catch { toast.error('Failed to upload photo'); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ''; }
  };

  return (
    <div data-testid="step-photo-upload">
      <input ref={fileRef} type="file" accept="image/*;capture=camera" onChange={handleFile} className="hidden" />
      <button className={`bench-touch-btn bench-touch-btn--secondary ${large ? 'bench-touch-btn--lg' : ''}`} onClick={() => fileRef.current?.click()} disabled={uploading}>
        <Camera size={large ? 20 : 14} /> {uploading ? 'Uploading...' : 'Photo'}
      </button>
    </div>
  );
};

// ==================== DESKTOP EXECUTION STEP CARD ====================

const ExecutionStepCard = ({ step, stepIndex, isActive, runId, onRefresh }) => {
  const [expanded, setExpanded] = useState(false);
  const [showNotes, setShowNotes] = useState(false);
  const [showDeviation, setShowDeviation] = useState(false);
  const [toggling, setToggling] = useState(false);
  const stepNum = stepIndex + 1;

  const toggleComplete = async () => {
    setToggling(true);
    try {
      await api.updateRunStep(runId, stepNum, step.completed ? 'uncomplete' : 'complete');
      onRefresh();
    } catch { toast.error('Failed to update step'); }
    finally { setToggling(false); }
  };

  return (
    <div
      className={`execution-step-card ${isActive ? 'active' : ''} ${step.completed ? 'completed' : ''}`}
      data-testid={`execution-step-${stepNum}`}
    >
      <div className="flex items-start gap-3">
        <button
          onClick={toggleComplete}
          disabled={toggling}
          className={`flex-shrink-0 w-8 h-8 rounded-lg flex items-center justify-center transition-all ${
            step.completed
              ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/40'
              : 'bg-slate-700/50 text-slate-500 border border-slate-600/50 hover:border-indigo-500/50 hover:text-indigo-400'
          }`}
          data-testid={`step-${stepNum}-checkbox`}
        >
          {step.completed ? <CheckCircle size={18} weight="fill" /> : <Circle size={18} />}
        </button>

        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between">
            <p className={`text-sm font-medium ${step.completed ? 'text-slate-500 line-through' : 'text-slate-200'}`}>
              {step.title || `Step ${stepNum}`}
            </p>
            <div className="flex items-center gap-2">
              {step.completed && step.completed_at && (
                <span className="text-xs text-emerald-400/70">{new Date(step.completed_at).toLocaleTimeString()}</span>
              )}
              {(step.photos?.length > 0 || step.notes?.length > 0 || step.deviations?.length > 0) && (
                <button onClick={() => setExpanded(!expanded)} className="text-slate-500 hover:text-slate-300">
                  {expanded ? <CaretUp size={14} /> : <CaretDown size={14} />}
                </button>
              )}
            </div>
          </div>

          <p className={`mt-1 text-sm leading-relaxed ${step.completed ? 'text-slate-500' : 'text-slate-300'}`}>
            {step.instruction || `Step ${stepNum}`}
          </p>

          {step.safety_note && (
            <div className="flex items-start gap-2 mt-2 p-2 rounded-md bg-amber-500/10 border border-amber-500/20">
              <Warning size={14} className="text-amber-400 mt-0.5 flex-shrink-0" />
              <p className="text-xs text-amber-300">{step.safety_note}</p>
            </div>
          )}

          {step.duration_minutes && !step.completed && (
            <CountdownTimer durationMinutes={parseFloat(step.duration_minutes)} />
          )}

          {isActive && !step.completed && (
            <div className="flex flex-wrap items-center gap-2 mt-3">
              <StepPhotoUpload runId={runId} stepNum={stepNum} onUploaded={onRefresh} />
              <button className="bench-touch-btn bench-touch-btn--secondary" onClick={() => setShowNotes(!showNotes)}>
                <NoteBlank size={14} /> Note
              </button>
              <button className="bench-touch-btn bench-touch-btn--secondary" onClick={() => setShowDeviation(!showDeviation)}>
                <Warning size={14} /> Deviation
              </button>
            </div>
          )}

          {showNotes && <StepNoteForm runId={runId} stepNum={stepNum} onAdded={() => { onRefresh(); setShowNotes(false); }} />}
          {showDeviation && <StepDeviationForm runId={runId} stepNum={stepNum} onAdded={() => { onRefresh(); setShowDeviation(false); }} />}
        </div>
      </div>

      {expanded && (
        <div className="mt-3 ml-11 space-y-2">
          {step.notes?.map((n, i) => (
            <div key={i} className="text-xs p-2 rounded bg-slate-700/40 border border-slate-600/30">
              <span className="text-indigo-400">{n.by}</span>: {n.text}
              <span className="text-slate-500 ml-2">{new Date(n.at).toLocaleTimeString()}</span>
            </div>
          ))}
          {step.deviations?.map((d, i) => (
            <div key={i} className="text-xs p-2 rounded bg-amber-500/10 border border-amber-500/20">
              <p className="text-amber-300 font-medium">{d.text}</p>
              {d.expected && <p className="text-slate-400 mt-1">Expected: {d.expected} | Actual: {d.actual}</p>}
            </div>
          ))}
          {step.photos?.map((p, i) => (
            <div key={i} className="flex items-center gap-2 text-xs text-slate-400">
              <Image size={12} /> {p.filename} <span className="text-slate-600">by {p.by}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ==================== TABLET FOCUS STEP CARD ====================

const FocusStepCard = ({ step, stepIndex, totalSteps, runId, onRefresh, onPrev, onNext }) => {
  const [showNotes, setShowNotes] = useState(false);
  const [showDeviation, setShowDeviation] = useState(false);
  const [toggling, setToggling] = useState(false);
  const stepNum = stepIndex + 1;
  const swipeRef = useRef(null);

  useSwipe(onNext, onPrev, swipeRef);

  const toggleComplete = async () => {
    setToggling(true);
    try {
      await api.updateRunStep(runId, stepNum, step.completed ? 'uncomplete' : 'complete');
      onRefresh();
    } catch { toast.error('Failed to update step'); }
    finally { setToggling(false); }
  };

  return (
    <div ref={swipeRef} className="focus-step-card" data-testid={`focus-step-${stepNum}`}>
      {/* Step indicator */}
      <div className="flex items-center justify-between mb-4">
        <span className="text-sm text-slate-500 font-medium">Step {stepNum} of {totalSteps}</span>
        {step.completed && step.completed_at && (
          <span className="text-sm text-emerald-400 font-medium">
            Done at {new Date(step.completed_at).toLocaleTimeString()}
          </span>
        )}
      </div>

      {/* Title */}
      <h2 className={`text-2xl font-semibold mb-3 ${step.completed ? 'text-slate-500 line-through' : 'text-slate-100'}`}>
        {step.title || `Step ${stepNum}`}
      </h2>

      {/* Instruction */}
      <p className={`text-base leading-relaxed mb-4 ${step.completed ? 'text-slate-500' : 'text-slate-300'}`}>
        {step.instruction || ''}
      </p>

      {/* Safety note */}
      {step.safety_note && (
        <div className="flex items-start gap-3 p-4 rounded-xl bg-amber-500/10 border border-amber-500/20 mb-4">
          <Warning size={22} className="text-amber-400 mt-0.5 flex-shrink-0" />
          <p className="text-sm text-amber-300">{step.safety_note}</p>
        </div>
      )}

      {/* Timer */}
      {step.duration_minutes && !step.completed && (
        <CountdownTimer durationMinutes={parseFloat(step.duration_minutes)} large />
      )}

      {/* Big Checkbox */}
      <button
        onClick={toggleComplete}
        disabled={toggling}
        className={`focus-checkbox ${step.completed ? 'focus-checkbox--done' : ''}`}
        data-testid={`focus-step-${stepNum}-checkbox`}
      >
        {step.completed ? (
          <><CheckCircle size={28} weight="fill" /> Mark Incomplete</>
        ) : (
          <><Circle size={28} /> Mark Complete</>
        )}
      </button>

      {/* Action buttons */}
      {!step.completed && (
        <div className="grid grid-cols-3 gap-3 mt-4">
          <StepPhotoUpload runId={runId} stepNum={stepNum} onUploaded={onRefresh} large />
          <button className="bench-touch-btn bench-touch-btn--secondary bench-touch-btn--lg" onClick={() => setShowNotes(!showNotes)}>
            <NoteBlank size={20} /> Note
          </button>
          <button className="bench-touch-btn bench-touch-btn--secondary bench-touch-btn--lg" onClick={() => setShowDeviation(!showDeviation)}>
            <Warning size={20} /> Deviation
          </button>
        </div>
      )}

      {showNotes && <StepNoteForm runId={runId} stepNum={stepNum} large onAdded={() => { onRefresh(); setShowNotes(false); }} />}
      {showDeviation && <StepDeviationForm runId={runId} stepNum={stepNum} large onAdded={() => { onRefresh(); setShowDeviation(false); }} />}

      {/* Existing notes/deviations/photos inline */}
      {(step.notes?.length > 0 || step.deviations?.length > 0 || step.photos?.length > 0) && (
        <div className="mt-4 space-y-2">
          {step.notes?.map((n, i) => (
            <div key={i} className="text-sm p-3 rounded-lg bg-slate-700/40 border border-slate-600/30">
              <span className="text-indigo-400 font-medium">{n.by}</span>: {n.text}
            </div>
          ))}
          {step.deviations?.map((d, i) => (
            <div key={i} className="text-sm p-3 rounded-lg bg-amber-500/10 border border-amber-500/20">
              <p className="text-amber-300 font-medium">{d.text}</p>
              {d.expected && <p className="text-slate-400 mt-1 text-xs">Expected: {d.expected} | Actual: {d.actual}</p>}
            </div>
          ))}
          {step.photos?.map((p, i) => (
            <div key={i} className="flex items-center gap-2 text-sm text-slate-400">
              <Image size={16} /> {p.filename}
            </div>
          ))}
        </div>
      )}

      {/* Swipe navigation arrows */}
      <div className="focus-step-nav">
        <button
          onClick={onPrev}
          disabled={stepIndex <= 0}
          className="focus-nav-btn"
          data-testid="focus-prev-step"
        >
          <SkipBack size={24} />
        </button>
        <span className="text-sm text-slate-500">Swipe or tap to navigate</span>
        <button
          onClick={onNext}
          disabled={stepIndex >= totalSteps - 1}
          className="focus-nav-btn"
          data-testid="focus-next-step"
        >
          <SkipForward size={24} />
        </button>
      </div>
    </div>
  );
};

// ==================== MAIN COMPONENT ====================

const BenchMode = () => {
  const { id } = useParams();
  const [protocol, setProtocol] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeRun, setActiveRun] = useState(null);
  const [pastRuns, setPastRuns] = useState([]);
  const [starting, setStarting] = useState(false);
  const [activeStepIdx, setActiveStepIdx] = useState(0);
  const stepRefs = useRef({});
  const autoDetected = useTabletDetect();
  const [tabletMode, setTabletMode] = useState(false);

  // Sync auto-detect on mount
  useEffect(() => { setTabletMode(autoDetected); }, [autoDetected]);

  useEffect(() => { loadData(); }, [id]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [proto, runs] = await Promise.all([
        api.getProtocol(id),
        api.getProtocolRuns(id)
      ]);
      setProtocol(proto);
      setPastRuns(runs || []);
      const inProgress = runs?.find(r => r.status === 'in_progress');
      if (inProgress) {
        setActiveRun(inProgress);
        const firstUncompleted = inProgress.steps?.findIndex(s => !s.completed);
        setActiveStepIdx(firstUncompleted >= 0 ? firstUncompleted : 0);
      }
    } catch {
      toast.error('Failed to load protocol');
    } finally {
      setLoading(false);
    }
  };

  const startNewRun = async () => {
    setStarting(true);
    try {
      const run = await api.startProtocolRun(id);
      setActiveRun(run);
      setActiveStepIdx(0);
      toast.success('Execution started');
    } catch {
      toast.error('Failed to start execution');
    } finally {
      setStarting(false);
    }
  };

  const refreshRun = useCallback(async () => {
    if (!activeRun?.id) return;
    try {
      const updated = await api.getProtocolRun(activeRun.id);
      setActiveRun(updated);
      if (updated.status === 'completed') {
        toast.success('Protocol execution complete!');
      } else {
        const firstUncompleted = updated.steps?.findIndex(s => !s.completed);
        if (firstUncompleted >= 0) setActiveStepIdx(firstUncompleted);
      }
    } catch { /* ignore */ }
  }, [activeRun?.id]);

  const resumeRun = async (run) => {
    try {
      const fullRun = await api.getProtocolRun(run.id);
      setActiveRun(fullRun);
      const firstUncompleted = fullRun.steps?.findIndex(s => !s.completed);
      setActiveStepIdx(firstUncompleted >= 0 ? firstUncompleted : 0);
    } catch { toast.error('Failed to load run'); }
  };

  const scrollToStep = (idx) => {
    setActiveStepIdx(idx);
    stepRefs.current[idx]?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  };

  const goNext = useCallback(() => {
    setActiveStepIdx(prev => Math.min(prev + 1, (activeRun?.steps?.length || 1) - 1));
  }, [activeRun?.steps?.length]);

  const goPrev = useCallback(() => {
    setActiveStepIdx(prev => Math.max(prev - 1, 0));
  }, []);

  // ========== LOADING ==========
  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="spinner" /></div>;
  }

  if (!protocol) {
    return (
      <div className="text-center py-12">
        <p className="text-slate-400">Protocol not found</p>
        <Link to="/protocols" className="text-indigo-400 hover:underline mt-2 inline-block">Back to protocols</Link>
      </div>
    );
  }

  // ========== MODE TOGGLE BUTTON ===========
  const ModeToggle = () => (
    <button
      onClick={() => setTabletMode(!tabletMode)}
      className={`bench-mode-toggle ${tabletMode ? 'bench-mode-toggle--active' : ''}`}
      data-testid="tablet-mode-toggle"
      title={tabletMode ? 'Switch to Desktop view' : 'Switch to Tablet/Touch view'}
    >
      {tabletMode ? <Desktop size={18} /> : <DeviceTabletSpeaker size={18} />}
      <span className="hidden sm:inline ml-1.5 text-xs">
        {tabletMode ? 'Desktop' : 'Tablet'}
      </span>
    </button>
  );

  // ========== ACTIVE EXECUTION VIEW ==========
  if (activeRun) {
    const completedCount = activeRun.steps?.filter(s => s.completed).length || 0;
    const totalSteps = activeRun.steps?.length || 0;
    const progress = totalSteps > 0 ? Math.round((completedCount / totalSteps) * 100) : 0;
    const isComplete = activeRun.status === 'completed';

    return (
      <div className={tabletMode ? 'bench-tablet' : ''} data-testid="bench-mode-execution">
        {/* Header */}
        <div className="bench-header">
          <div className="flex items-center gap-3">
            <button
              onClick={() => setActiveRun(null)}
              className="bench-touch-btn bench-touch-btn--ghost"
              data-testid="back-to-runs"
            >
              <ArrowLeft size={tabletMode ? 24 : 20} />
            </button>
            <div>
              <h1 className={`font-semibold text-slate-100 ${tabletMode ? 'text-xl' : 'text-lg'}`}>{protocol.title}</h1>
              <p className="text-xs text-slate-500">
                Started by {activeRun.started_by_name} at {new Date(activeRun.started_at).toLocaleString()}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <ModeToggle />
            {isComplete && (
              <span className="px-3 py-1 rounded-full bg-emerald-500/15 text-emerald-400 text-xs font-medium border border-emerald-500/30" data-testid="run-complete-badge">
                Complete
              </span>
            )}
            <div className="text-right">
              <p className="text-xs text-slate-500">{completedCount}/{totalSteps} steps</p>
              <p className={`font-semibold text-slate-200 ${tabletMode ? 'text-base' : 'text-sm'}`}>{progress}%</p>
            </div>
            <div className={`bg-slate-700 rounded-full overflow-hidden ${tabletMode ? 'w-36 h-3' : 'w-28 h-2'}`}>
              <div
                className="h-full rounded-full transition-all duration-500"
                style={{
                  width: `${progress}%`,
                  background: isComplete
                    ? 'linear-gradient(90deg, #10B981, #34D399)'
                    : 'linear-gradient(90deg, #6366F1, #06B6D4)'
                }}
              />
            </div>
          </div>
        </div>

        {/* Step Navigation Pills */}
        <div className={`step-nav-pills ${tabletMode ? 'step-nav-pills--tablet' : ''}`} data-testid="step-nav-pills">
          {activeRun.steps?.map((s, idx) => (
            <button
              key={idx}
              onClick={() => tabletMode ? setActiveStepIdx(idx) : scrollToStep(idx)}
              className={`step-pill ${tabletMode ? 'step-pill--tablet' : ''} ${s.completed ? 'completed' : idx === activeStepIdx ? 'active' : ''}`}
              data-testid={`step-pill-${idx + 1}`}
            >
              {s.completed ? <Check size={tabletMode ? 14 : 10} weight="bold" /> : idx + 1}
            </button>
          ))}
        </div>

        {/* TABLET FOCUS VIEW */}
        {tabletMode && !isComplete && (
          <FocusStepCard
            step={activeRun.steps[activeStepIdx]}
            stepIndex={activeStepIdx}
            totalSteps={totalSteps}
            runId={activeRun.id}
            onRefresh={refreshRun}
            onPrev={goPrev}
            onNext={goNext}
          />
        )}

        {/* DESKTOP LIST VIEW */}
        {!tabletMode && !isComplete && (
          <div className="space-y-2 mt-4">
            {activeRun.steps?.map((step, idx) => (
              <div key={idx} ref={el => stepRefs.current[idx] = el}>
                <ExecutionStepCard
                  step={step}
                  stepIndex={idx}
                  isActive={idx === activeStepIdx}
                  runId={activeRun.id}
                  onRefresh={refreshRun}
                />
              </div>
            ))}
          </div>
        )}

        {/* Completion Summary */}
        {isComplete && (
          <div className={`card-flat mt-6 border-emerald-500/20 ${tabletMode ? 'p-8' : 'p-6'}`} data-testid="run-summary">
            <div className="flex items-center gap-3 mb-4">
              <CheckCircle size={tabletMode ? 36 : 28} weight="fill" className="text-emerald-400" />
              <div>
                <h3 className={`font-semibold text-slate-100 ${tabletMode ? 'text-xl' : 'text-base'}`}>Execution Complete</h3>
                <p className="text-xs text-slate-400">
                  Completed at {activeRun.completed_at ? new Date(activeRun.completed_at).toLocaleString() : 'N/A'}
                </p>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div className={`text-center rounded-lg bg-slate-700/30 ${tabletMode ? 'p-5' : 'p-3'}`}>
                <p className={`font-bold text-indigo-400 ${tabletMode ? 'text-3xl' : 'text-xl'}`}>{totalSteps}</p>
                <p className={`text-slate-400 ${tabletMode ? 'text-sm mt-1' : 'text-xs'}`}>Steps</p>
              </div>
              <div className={`text-center rounded-lg bg-slate-700/30 ${tabletMode ? 'p-5' : 'p-3'}`}>
                <p className={`font-bold text-amber-400 ${tabletMode ? 'text-3xl' : 'text-xl'}`}>
                  {activeRun.steps?.reduce((sum, s) => sum + (s.deviations?.length || 0), 0)}
                </p>
                <p className={`text-slate-400 ${tabletMode ? 'text-sm mt-1' : 'text-xs'}`}>Deviations</p>
              </div>
              <div className={`text-center rounded-lg bg-slate-700/30 ${tabletMode ? 'p-5' : 'p-3'}`}>
                <p className={`font-bold text-cyan-400 ${tabletMode ? 'text-3xl' : 'text-xl'}`}>
                  {activeRun.steps?.reduce((sum, s) => sum + (s.photos?.length || 0), 0)}
                </p>
                <p className={`text-slate-400 ${tabletMode ? 'text-sm mt-1' : 'text-xs'}`}>Photos</p>
              </div>
            </div>
            <button className={`bench-touch-btn bench-touch-btn--secondary w-full mt-4 justify-center ${tabletMode ? 'bench-touch-btn--lg' : ''}`} onClick={() => setActiveRun(null)} data-testid="back-to-overview-btn">
              Back to Overview
            </button>
          </div>
        )}
      </div>
    );
  }

  // ========== LANDING VIEW ==========
  return (
    <div className={tabletMode ? 'bench-tablet' : ''} data-testid="bench-mode-page">
      <div className="flex items-center gap-4 mb-6">
        <Link to={`/protocols/${id}`} className="bench-touch-btn bench-touch-btn--ghost">
          <ArrowLeft size={tabletMode ? 24 : 20} />
        </Link>
        <div className="flex-1">
          <h1 className={`font-semibold text-slate-100 ${tabletMode ? 'text-2xl' : 'text-xl'}`}>{protocol.title}</h1>
          <p className={`text-slate-500 mt-0.5 ${tabletMode ? 'text-base' : 'text-sm'}`}>Bench Mode — Interactive Execution</p>
        </div>
        <ModeToggle />
      </div>

      {/* Protocol Info */}
      <div className={`card-flat mb-6 ${tabletMode ? 'p-8' : 'p-6'}`}>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          {[
            { label: 'Steps', value: protocol.steps?.length || 0 },
            { label: 'Est. Duration', value: protocol.duration_minutes ? `${protocol.duration_minutes} min` : 'N/A' },
            { label: 'Materials', value: protocol.materials?.length || 0 },
            { label: 'Past Runs', value: pastRuns.length },
          ].map(({ label, value }) => (
            <div key={label}>
              <p className="text-xs text-slate-500 uppercase tracking-wider">{label}</p>
              <p className={`font-semibold text-slate-200 mt-1 ${tabletMode ? 'text-2xl' : 'text-lg'}`}>{value}</p>
            </div>
          ))}
        </div>
        {protocol.description && (
          <p className={`text-slate-400 mt-4 pt-4 border-t border-slate-700/50 ${tabletMode ? 'text-base' : 'text-sm'}`}>{protocol.description}</p>
        )}
      </div>

      {/* Start Button */}
      <button
        className={`bench-start-btn ${tabletMode ? 'bench-start-btn--tablet' : ''}`}
        onClick={startNewRun}
        disabled={starting || !protocol.steps?.length}
        data-testid="start-new-run-btn"
      >
        {starting ? (
          <><div className="spinner w-6 h-6" /> Starting...</>
        ) : (
          <><Play size={tabletMode ? 28 : 20} weight="fill" /> Start New Execution</>
        )}
      </button>

      {/* Steps Preview */}
      <div className="mb-8">
        <h3 className="text-sm font-medium text-slate-400 uppercase tracking-wider mb-3">Protocol Steps</h3>
        <div className="space-y-1">
          {protocol.steps?.map((step, idx) => (
            <div key={idx} className={`flex items-start gap-3 rounded-lg bg-slate-800/40 border border-slate-700/30 ${tabletMode ? 'p-4' : 'p-3'}`}>
              <div className="protocol-step-number text-xs">{idx + 1}</div>
              <div className="flex-1 min-w-0">
                <p className={`text-slate-300 ${tabletMode ? 'text-base' : 'text-sm'}`}>{step.instruction || step.title || step.description || step}</p>
                {step.duration_minutes && (
                  <span className="text-xs text-slate-500 flex items-center gap-1 mt-1">
                    <Clock size={12} /> {step.duration_minutes} min
                  </span>
                )}
              </div>
            </div>
          ))}
          {(!protocol.steps || protocol.steps.length === 0) && (
            <p className="text-sm text-slate-500 text-center py-6">No steps defined. Add steps to the protocol before executing.</p>
          )}
        </div>
      </div>

      {/* Past Runs */}
      {pastRuns.length > 0 && (
        <div>
          <h3 className="text-sm font-medium text-slate-400 uppercase tracking-wider mb-3">Run History</h3>
          <div className="space-y-2">
            {pastRuns.map(run => {
              const completed = run.steps?.filter(s => s.completed).length || 0;
              const total = run.steps?.length || 0;
              const pct = total > 0 ? Math.round((completed / total) * 100) : 0;
              return (
                <button
                  key={run.id}
                  onClick={() => resumeRun(run)}
                  className={`w-full text-left rounded-lg bg-slate-800/50 border border-slate-700/40 hover:border-indigo-500/30 transition-all ${tabletMode ? 'p-5' : 'p-4'}`}
                  data-testid={`past-run-${run.id}`}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                        run.status === 'completed' ? 'bg-emerald-500/15 text-emerald-400' : 'bg-amber-500/15 text-amber-400'
                      }`}>
                        {run.status === 'completed' ? 'Completed' : 'In Progress'}
                      </span>
                      <p className={`text-slate-300 mt-1 ${tabletMode ? 'text-base' : 'text-sm'}`}>{run.started_by_name}</p>
                      <p className="text-xs text-slate-500">{new Date(run.started_at).toLocaleString()}</p>
                    </div>
                    <div className="text-right">
                      <p className={`font-semibold text-slate-300 ${tabletMode ? 'text-lg' : 'text-sm'}`}>{pct}%</p>
                      <p className="text-xs text-slate-500">{completed}/{total} steps</p>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default BenchMode;
